import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';

// One control that names its own state. The trigger always reads the range that
// was actually applied, so there is no state in which the page is scoped to
// something the control doesn't say. Presets and absolute bounds live inside it
// rather than beside it, because they set the same one thing.
//
// It sits apart from the category filters on purpose: this control costs a fetch
// and can come back short, and those only re-filter rows already in memory.
// Also the width the open/close effect measures against the viewport.
const POP_W = 300;
const VIEWPORT_MARGIN = 8;

const StyledDiv = styledComponent('div', {
  position: 'relative',
  display: 'inline-block',

  '& .frp-trigger': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    padding: theme.spacing.smallButtonPadding,
    background: theme.colors.bgCard,
    border: theme.borders.input,
    borderRadius: theme.radii.input,
    ...theme.typography.smallButton,
    color: theme.colors.textBody,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  '& .frp-trigger-label': {
    fontWeight: theme.fontWeights.semibold,
  },
  '& .frp-trigger-caret': {
    color: theme.colors.textMuted,
    fontSize: theme.sizes.iconSm,
  },

  // Aligned to whichever edge keeps it on screen. It used to be pinned right,
  // which put it off the left of the viewport once the trigger moved to the start
  // of the scope bar.
  '& .frp-pop': {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    zIndex: 20,
    width: `${POP_W}px`,
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    boxShadow: theme.shadows.dropdown,
    padding: theme.spacing.bodyPadding,
    textAlign: 'left',
  },
  '& .frp-pop-end': {
    left: 'auto',
    right: 0,
  },
  // Narrower than the popover itself: neither edge can hold it, so it just fits.
  '@media (max-width: 400px)': {
    '& .frp-pop': {
      width: 'calc(100vw - 32px)',
    },
  },

  '& .frp-presets': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.gapSm,
    marginBottom: theme.spacing.gapXl,
  },
  '& .frp-preset': {
    padding: '4px 10px',
    borderRadius: theme.radii.pill,
    border: `1px solid ${theme.colors.border}`,
    background: theme.colors.bgCard,
    color: theme.colors.textBody,
    ...theme.typography.badge,
    cursor: 'pointer',
  },
  '& .frp-preset-on': {
    background: theme.colors.primary,
    borderColor: theme.colors.primary,
    color: theme.colors.white,
  },

  '& .frp-field': {
    display: 'block',
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.gapMd,
  },
  '& .frp-field input': {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: theme.spacing.gapSm,
    padding: theme.spacing.inputPadding,
    border: theme.borders.input,
    borderRadius: theme.radii.input,
    ...theme.typography.body,
    color: theme.colors.textBody,
  },
  '& .frp-hint': {
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.gapMd,
  },
  '& .frp-warn': {
    ...theme.typography.hint,
    color: theme.colors.warningCalloutAccent,
    marginBottom: theme.spacing.gapMd,
  },

  '& .frp-actions': {
    display: 'flex',
    gap: theme.spacing.gapMd,
  },
  '& .frp-apply': {
    flex: 1,
    padding: theme.spacing.smallButtonPadding,
    borderRadius: theme.radii.input,
    border: 'none',
    background: theme.colors.primary,
    color: theme.colors.white,
    ...theme.typography.smallButton,
    cursor: 'pointer',
    '&:disabled': {
      background: theme.colors.disabled,
      cursor: 'not-allowed',
    },
  },
  '& .frp-reset': {
    flex: 1,
    padding: theme.spacing.smallButtonPadding,
    borderRadius: theme.radii.input,
    border: `1px solid ${theme.colors.border}`,
    background: theme.colors.bgCard,
    color: theme.colors.textBody,
    ...theme.typography.smallButton,
    cursor: 'pointer',
  },
});

export default function TimeRangePicker({
  label,
  presets,
  activePreset,
  onPreset,
  fromStr,
  toStr,
  onFromChange,
  onToChange,
  minStr,
  maxStr,
  toWithoutSince,
  reversedRange,
  clampNotice,
  onSearch,
  onReset,
}) {
  const [open, setOpen] = useState(false);
  const [alignEnd, setAlignEnd] = useState(false);
  const wrapRef = useRef(null);

  // Measured on open rather than assumed from where the trigger sits today, so
  // moving the control again can't push its popover off screen.
  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const { left } = wrapRef.current.getBoundingClientRect();
    setAlignEnd(left + POP_W > window.innerWidth - VIEWPORT_MARGIN);
  }, [open]);

  // A popover that outlives a click elsewhere hides the page it's describing.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Applying is what the popover is for, so it closes on the way out and the
  // trigger — which reads the applied range — becomes the confirmation.
  const apply = (fn) => () => {
    fn();
    setOpen(false);
  };

  return (
    <StyledDiv ref={wrapRef}>
      <button
        type="button"
        className="frp-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden>🕑</span>
        <span className="frp-trigger-label">{label}</span>
        <span className="frp-trigger-caret" aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <div
          className={`frp-pop${alignEnd ? ' frp-pop-end' : ''}`}
          role="dialog"
          aria-label="Time range"
        >
          <div className="frp-presets">
            {presets.map((p) => (
              <button
                type="button"
                key={p.key}
                className={`frp-preset${
                  activePreset === p.key ? ' frp-preset-on' : ''
                }`}
                aria-pressed={activePreset === p.key}
                onClick={apply(() => onPreset(p.key))}
              >
                {p.label}
              </button>
            ))}
          </div>

          <label className="frp-field" htmlFor="frp-since">
            <span>Since</span>
            <input
              id="frp-since"
              type="datetime-local"
              value={fromStr}
              min={minStr}
              max={maxStr}
              onChange={(e) => onFromChange(e.target.value)}
            />
          </label>
          <label className="frp-field" htmlFor="frp-to">
            <span>To</span>
            <input
              id="frp-to"
              type="datetime-local"
              value={toStr}
              min={fromStr || minStr}
              max={maxStr}
              onChange={(e) => onToChange(e.target.value)}
            />
          </label>

          {/* datetime-local has no placeholder, so empty has to say what it means. */}
          <div className="frp-hint">Leave both empty for today so far.</div>
          {toWithoutSince && (
            <div className="frp-warn" role="status">
              Add a Since to search a range — a To on its own can&apos;t be
              applied.
            </div>
          )}
          {reversedRange && (
            <div className="frp-warn" role="status">
              To is earlier than Since — fix the range to search it.
            </div>
          )}
          {clampNotice && (
            <div className="frp-warn" role="status">
              {clampNotice}
            </div>
          )}

          <div className="frp-actions">
            <button
              type="button"
              className="frp-apply"
              onClick={apply(onSearch)}
              disabled={reversedRange}
            >
              Search
            </button>
            <button
              type="button"
              className="frp-reset"
              onClick={apply(onReset)}
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </StyledDiv>
  );
}

TimeRangePicker.propTypes = {
  // The range ACTUALLY applied, never what's currently typed in the fields.
  label: PropTypes.string.isRequired,
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  // null when the applied range came from the date fields rather than a preset.
  activePreset: PropTypes.string,
  onPreset: PropTypes.func.isRequired,
  fromStr: PropTypes.string.isRequired,
  toStr: PropTypes.string.isRequired,
  onFromChange: PropTypes.func.isRequired,
  onToChange: PropTypes.func.isRequired,
  minStr: PropTypes.string.isRequired,
  maxStr: PropTypes.string.isRequired,
  toWithoutSince: PropTypes.bool,
  // Blocks the search outright: no window can be built from an inverted range.
  reversedRange: PropTypes.bool,
  // Set when the typed range reaches outside what the server will return.
  clampNotice: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

TimeRangePicker.defaultProps = {
  activePreset: null,
  toWithoutSince: false,
  reversedRange: false,
  clampNotice: null,
};
